import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Button,
  Icon,
  Loader,
  Item,
  Label,
  Modal,
  Tab,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import BookItem from '../../components/book/bookItem';
import BookItemPlaceholder from '../../components/book/bookItemPlaceholder';
import ChapterUploadModal from '../content/chapter/upload/chapterUploadModal';
import CreateFirstBookSegment from './createFirstBookSegment';
import ActiveReaders from '../../components/statistics/activeReaders';
import WelcomeMessage from '../../components/standard-messages/welcomeMessage';
import GetMoreReadersMessage from '../../components/standard-messages/getMoreReadersMessage';
import UnlockSignupBonusMessage from '../../components/standard-messages/unlockSignupBonusMessage';
import NewBook from '../book/newBook';
import SampleChaptersTab from '../sampleChapters/sampleChaptersTab';

import { updateWritingList, updateCollaborationList } from '../../modules/book';
import { fetchReaderStatistics } from '../../modules/reader';

import privacy from '../../config/privacy';
import { getTheme } from '../../constants/theme';
import { authUtil, productCapUtil, uxAnalyticsUtil } from '../../utils';

const READER_STATS_INTERVAL = 60000;

const Dashboard = styled.div`
  --library-surface: ${({ theme }) =>
    (theme.navigation || getTheme('bright').navigation).surface};
  --library-border: ${({ theme }) =>
    (theme.navigation || getTheme('bright').navigation).border};
  --library-text: ${({ theme }) =>
    (theme.navigation || getTheme('bright').navigation).heading};
  --library-muted: ${({ theme }) =>
    (theme.navigation || getTheme('bright').navigation).text};
  --library-accent: ${({ theme }) =>
    (theme.navigation || getTheme('bright').navigation).accent};
  --library-focus: ${({ theme }) =>
    (theme.navigation || getTheme('bright').navigation).focus};
  padding: 0 24px 24px;
  max-width: 1280px;
  margin: 0 auto;
  min-width: 0;

  .dashboard-toolbar .ui.button:not(.primary) {
    color: var(--library-text);
    background: var(--library-surface);
    box-shadow: inset 0 0 0 1px var(--library-border);
  }
  .dashboard-toolbar {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
  }
  #books-length-label {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  #books-length-label .ui.label {
    margin: 0;
    background: var(--library-surface);
    color: var(--library-muted) !important;
  }
  .ui.secondary.pointing.menu {
    display: flex;
    border-color: var(--library-border);
    margin-bottom: 8px;
  }
  .ui.secondary.pointing.menu .item {
    min-height: 48px;
    justify-content: center;
    color: var(--library-muted) !important;
  }
  .ui.secondary.pointing.menu .active.item {
    color: var(--library-accent) !important;
    border-color: var(--library-accent);
  }
  .ui.items {
    margin: 0;
  }
  && .ui.items > .manuscript-book-item {
    padding: 24px 0 !important;
    gap: 20px;
    border-color: var(--library-border);
  }
  .ui.items > .manuscript-book-item > .content {
    min-width: 0;
    padding: 0 !important;
  }
  .manuscript-book-item .header {
    gap: 8px;
    width: 100%;
    color: var(--library-text) !important;
    line-height: 1.35;
  }
  .manuscript-book-item .book-item-reviews {
    flex: 0 0 190px;
    width: 190px;
    align-self: center;
  }
  .book-item-rating.ui.label {
    color: var(--library-accent) !important;
    background: var(--library-surface) !important;
  }
  .book-item-reviews td:first-child {
    white-space: nowrap;
    padding-right: 12px;
  }
  .manuscript-book-item .description,
  .manuscript-book-item .extra:not(.book-item-metrics) {
    max-width: 80ch;
  }
  .book-item-title {
    color: inherit !important;
  }
  .book-item-title:hover {
    text-decoration: underline;
  }
  .book-item-title,
  .book-item-reference {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .manuscript-book-item .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 8px;
    color: var(--library-muted) !important;
    line-height: 1.5;
  }
  .manuscript-book-item .extra {
    color: var(--library-muted) !important;
    overflow-wrap: anywhere;
  }
  .book-item-length {
    margin: 0 !important;
  }
  && .manuscript-book-item .book-item-metrics {
    display: flex !important;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }
  && .book-item-metrics .ui.label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 52px;
    min-height: 44px;
    margin: 0 !important;
    background: var(--library-surface) !important;
    color: var(--library-accent) !important;
    border-color: var(--library-border) !important;
  }
  a:focus-visible,
  button:focus-visible,
  .item:focus-visible {
    outline: 2px solid var(--library-focus);
    outline-offset: 3px;
  }
  @media (max-width: 767px) {
    padding: 0 16px 16px;
    .dashboard-toolbar {
      flex-direction: column-reverse;
      align-items: stretch;
      gap: 12px;
      margin-bottom: 16px;
    }
    .ui.secondary.pointing.menu .item {
      flex: 1 1 0;
      min-width: 0;
      padding: 12px 6px;
      font-size: 12px;
      text-align: center;
      line-height: 1.4;
      overflow-wrap: anywhere;
    }
    && .ui.items > .manuscript-book-item {
      gap: 0;
      padding: 20px 0 !important;
    }
    .manuscript-book-item .header {
      font-size: 18px !important;
    }
    .manuscript-book-item .meta {
      font-size: 13px;
      margin-top: 8px;
    }
    .manuscript-book-item .extra {
      font-size: 13px;
      line-height: 1.55;
    }
    .manuscript-book-item .book-item-length {
      flex-basis: 100%;
    }
  }
`;

const DashboardActions = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  gap: 8px;

  .ui.button {
    min-height: 44px;
    margin: 0;
  }

  @media only screen and (max-width: 767px) {
    flex-wrap: wrap;

    .ui.button {
      flex: 1 1 auto;
      padding-right: 12px;
      padding-left: 12px;
    }
  }
`;

export const MyManuscripts = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const refreshDashBoardInterval = useRef<ReturnType<typeof setInterval> | undefined>();

  const user = useSelector(state => state.user.user);
  const activeAccount = useSelector(state => state.account.activeAccount);
  const isFetchingCollaborationsList = useSelector(
    state => state.book.isFetchingCollaborationsList
  );
  const isFetchingWritingList = useSelector(
    state => state.book.isFetchingWritingList
  );
  const userProfile = useSelector(state => state.user.userProfile);
  const writing = useSelector(state => state.book.writing);
  const collaborating = useSelector(state => state.book.collaborating);
  const readerStatistics = useSelector(state => state.reader.readerStatistics);

  const [showChapterUpload, setShowChapterUpload] = useState(false);
  const [showModal, setShowModal] = useState<boolean | string>(false);

  useEffect(() => {
    uxAnalyticsUtil.trackPageView(location.pathname);
    const doFetchReaderStatistics = async () => {
      const idToken = await authUtil.getFreshIdToken();
      dispatch(fetchReaderStatistics(idToken));
    };

    doFetchReaderStatistics();
    refreshDashBoardInterval.current = setInterval(
      doFetchReaderStatistics,
      READER_STATS_INTERVAL
    );
    return () => {
      // clear interval when component unmounts
      if (refreshDashBoardInterval.current) clearInterval(refreshDashBoardInterval.current);
    };
  }, []);

  useEffect(() => {
    // set up dashboard again when activeAccount changes
    setupDashboard();
  }, [activeAccount]);

  const setupDashboard = async () => {
    const idToken = await authUtil.getFreshIdToken();
    if (idToken && userProfile) {
      dispatch(
        updateWritingList({
          idToken,
          accountId: activeAccount
            ? activeAccount._id
            : userProfile.account._id,
        })
      );
      dispatch(
        updateCollaborationList({
          idToken,
        })
      );
    }
  };

  const toggleChapterUpload = () => {
    setShowChapterUpload(!showChapterUpload);
    if (!showChapterUpload) {
      uxAnalyticsUtil.trackEvent({
        category: 'Books',
        action: 'clicked-add-book',
        label: 'upload',
      });
    }
  };

  const handleCreateBookClick = () => {
    uxAnalyticsUtil.trackEvent({
      category: 'Books',
      action: 'clicked-add-book',
      label: 'create',
    });
    setShowModal('create-book');
  };

  const renderBooksList = ({
    books = [] as Array<{ _id: string; author?: { _id: string } }>,
    isLoading = false,
    showUpload = false,
  }) => {
    if (isLoading) {
      // Render placeholder if we're still loading
      return [
        <BookItemPlaceholder key="first-placeholder" />,
        <BookItemPlaceholder key="second-placeholder" />,
        <BookItemPlaceholder key="third-placeholder" />,
      ];
    }
    if (books?.length > 0) {
      // Render books if any
      const booksList = books?.map(book => {
        return <BookItem key={book?._id} book={book} />;
      });
      return <Item.Group divided>{booksList}</Item.Group>;
    }
    if (showUpload) {
      // Otherwise, render the upload placeholder to incite the user to start testing a manuscript
      const result = [
        <CreateFirstBookSegment
          key="create-first-book"
          onCreateManuscript={handleCreateBookClick}
        />,
        <WelcomeMessage
          key="welcome"
          toggleChapterUpload={toggleChapterUpload}
        />,
      ];
      if (userProfile?.wasReferred && !userProfile?.signupReferralBonus) {
        result.push(
          <UnlockSignupBonusMessage
            key="unlock-bonus"
            userProfile={userProfile}
            user={user}
            writing={writing}
          />
        );
      }
      return result;
    }
    return null;
  };

  const hideModal = () => setShowModal(false);

  const getCounts = useCallback(
    () => productCapUtil.getBookCounts(writing),
    [writing]
  );

  const { unpublishedCount, privateCount, closedCount, listedCount } =
    getCounts();

  if (!userProfile) {
    return <Loader />;
  }

  const panes = [
    {
      menuItem: t('OwnBooks'),
      render: () => (
        <Tab.Pane as="div" key="own">
          <Item.Group link divided aria-busy={isFetchingWritingList}>
            {renderBooksList({
              books: writing,
              showUpload: true,
              isLoading: isFetchingWritingList,
            })}
          </Item.Group>
        </Tab.Pane>
      ),
    },
    {
      menuItem: t('Collaborations'),
      render: () => (
        <Tab.Pane as="div" key="collabs">
          <Item.Group link divided aria-busy={isFetchingCollaborationsList}>
            {renderBooksList({
              books: collaborating,
              showUpload: false,
              isLoading: isFetchingCollaborationsList,
            })}
          </Item.Group>
        </Tab.Pane>
      ),
    },
    {
      menuItem: t('SampleChapters', 'Sample chapters'),
      render: () => (
        <Tab.Pane as="div" key="sample-chapters">
          <SampleChaptersTab />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <Dashboard>
      {writing && writing?.length > 0 && (
        <ActiveReaders readerStatistics={readerStatistics} />
      )}
      <div>
        <div className="dashboard-toolbar">
          <div id="books-length-label">
            {unpublishedCount > 0 && (
              <Label
                title={t('PrivacyLevel_state_unpublished')}
                icon={privacy.privacyIcons.unpublished}
                content={`${unpublishedCount} ${t('PrivacyLevel_state_unpublished')}`}
              />
            )}
            {privateCount > 0 && (
              <Label
                title={t('PrivacyLevel_state_private')}
                icon={privacy.privacyIcons.private}
                content={`${privateCount} ${t('PrivacyLevel_state_private')}`}
              />
            )}
            {closedCount > 0 && (
              <Label
                title={t('PrivacyLevel_state_closed')}
                icon={privacy.privacyIcons.closed}
                content={`${closedCount} ${t('PrivacyLevel_state_closed')}`}
              />
            )}
            {listedCount > 0 && (
              <Label
                title={t('PrivacyLevel_state_listed')}
                icon={privacy.privacyIcons.listed}
                content={`${listedCount} ${t('PrivacyLevel_state_listed')}`}
              />
            )}
          </div>
          <DashboardActions>
            <Button icon onClick={toggleChapterUpload}>
              <Icon name="upload" />
              {t('Import')}
            </Button>
            <Button primary icon onClick={handleCreateBookClick}>
              <Icon name="add" />
              {t('Create')}
            </Button>
          </DashboardActions>
        </div>
      </div>
      <div>
        <Tab menu={{ secondary: true, pointing: true }} panes={panes} />
        {userProfile?.account?.product?.internalId === 'free' && [
          <GetMoreReadersMessage
            key="get-more-readers"
            user={user}
            userProfile={userProfile}
          />,
          <Link
            key="check-bonus"
            style={{ float: 'right' }}
            to="/account/reader-bonus"
          >
            <em>{t('CheckBonus')}</em>
          </Link>,
        ]}
      </div>
      {showChapterUpload && (
        <ChapterUploadModal
          createBook
          open
          onClose={toggleChapterUpload}
          toggleOpen={toggleChapterUpload}
        />
      )}
      {showModal === 'create-book' && (
        <Modal open closeIcon onClose={hideModal}>
          <Modal.Content>
            <NewBook />
          </Modal.Content>
        </Modal>
      )}
    </Dashboard>
  );
};

export default MyManuscripts;
